import React, { useEffect, useState } from "react";
import { createProgram, getAssignableUsers, getPrograms, setProgramLead } from "./api";
import ProgramDetail from "./ProgramDetail";
import "./programs.css";

const FLAG_LABEL = { green: "On track", yellow: "At risk", red: "Blocked" };

export function StatusFlag({ flag, comment }) {
  return (
    <span className={`pg-flag pg-flag-${flag}`} title={comment || ""}>
      {FLAG_LABEL[flag] || flag}
    </span>
  );
}

export function PhaseStrip({ phases, activeId, onSelect }) {
  return (
    <div className="pg-phase-strip">
      {phases.map((p) => (
        <div className="pg-phase-seg" key={p.id}>
          <div
            className={`pg-phase-box status-${phaseStatus(p)} ${activeId === p.id ? "active" : ""}`}
            onClick={onSelect ? () => onSelect(p) : undefined}
          >
            <div className="name">
              P{p.number} · {p.name}
            </div>
            <div className="pg-small pg-muted">
              {p.percent_complete}% · gate {fmtDate(p.gate_date || (p.gate && p.gate.decision_date))}
            </div>
          </div>
          <div
            className={`pg-gate-diamond ${p.gate_outcome || (p.gate && p.gate.outcome) || ""}`}
            title={gateTitle(p)}
          />
        </div>
      ))}
    </div>
  );
}

// Phase color state:
//   go -> green (gate passed), no_go -> red (gate failed),
//   conditional_go -> amber (passed with unresolved conditions),
//   otherwise: blue if the phase is currently active (today in range, or work
//   started but gate not yet decided), grey if planned/not started.
function phaseStatus(p) {
  const outcome = p.gate_outcome || (p.gate && p.gate.outcome);
  if (outcome === "go" || outcome === "no_go" || outcome === "conditional_go") {
    return outcome;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = p.start ? new Date(p.start + "T00:00:00") : null;
  const end = p.end ? new Date(p.end + "T00:00:00") : null;
  const inWindow = start && end && start <= today && today <= end;
  const started = (p.percent_complete || 0) > 0;
  if (inWindow || started) return "active";
  return "planned";
}

function gateTitle(p) {
  const outcome = p.gate_outcome || (p.gate && p.gate.outcome);
  if (outcome) return `Gate outcome: ${outcome.replace("_", " ")}`;
  const defined = p.gate_defined !== undefined ? p.gate_defined : p.gate && p.gate.is_defined;
  return defined ? "Gate open" : "Gate not yet defined";
}

export function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d + "T00:00:00");
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function ProgramCard({ p, onOpen }) {
  const undefinedGates = p.phases.filter((ph) => !ph.gate_defined).length;
  return (
    <div className="pg-card pg-card-click" onClick={() => onOpen(p.id)}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", minWidth: 0 }}>
          <strong style={{ fontSize: 15 }}>
            {p.code} — {p.title}
          </strong>
          <StatusFlag flag={p.status_flag} comment={p.status_comment} />
        </div>
        <span className="pg-muted pg-small">{p.percent_complete}% complete</span>
      </div>
      <div className="pg-progress" style={{ margin: "10px 0" }}>
        <div style={{ width: `${p.percent_complete}%` }} />
      </div>
      {p.status_comment && (
        <div className="pg-small pg-muted" style={{ marginBottom: 4 }}>
          {p.status_comment}
        </div>
      )}
      <PhaseStrip phases={p.phases} />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          borderTop: "1px solid var(--pg-border)",
          marginTop: 12,
          paddingTop: 10,
        }}
        className="pg-small"
      >
        <span className="pg-muted">
          Completion criteria: {p.criteria_met.met} of {p.criteria_met.total} met
          {p.criteria_undefined > 0 && (
            <span className="pg-badge pg-badge-red" style={{ marginLeft: 8 }}>
              {p.criteria_undefined} undefined
            </span>
          )}
          {undefinedGates > 0 && (
            <span className="pg-badge pg-badge-red" style={{ marginLeft: 6 }}>
              {undefinedGates} gate{undefinedGates > 1 ? "s" : ""} undefined
            </span>
          )}
        </span>
        {p.top_risk && (
          <span className="pg-muted" title={p.top_risk.description}>
            Top risk {p.top_risk.ref_id} · score {p.top_risk.score}
          </span>
        )}
      </div>
    </div>
  );
}

function CreateProgramModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    code: "",
    title: "",
    goal: "",
    application: "",
    technical_background: "",
    start_date: new Date().toISOString().slice(0, 10),
    num_phases: 4,
  });
  const [owner, setOwner] = useState("");
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    getAssignableUsers()
      .then((data) => setUsers(Array.isArray(data) ? data : data.results || []))
      .catch(() => setUsers([]));
  }, []);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const created = await createProgram({ ...form, num_phases: Number(form.num_phases) });
      if (owner) {
        try {
          await setProgramLead(created.id, owner);
        } catch (e) {
          // Program was created; lead assignment failed (e.g. permissions).
          // Surface it but still open the program.
          setError("Program created, but setting the owner failed: " + e.message);
        }
      }
      onCreated(created.id);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="pg-modal-back" onClick={onClose}>
      <div className="pg-modal" onClick={(e) => e.stopPropagation()}>
        <h3>New development program</h3>
        <div className="pg-field">
          <label>Program code</label>
          <input value={form.code} onChange={set("code")} placeholder="AF-02" />
        </div>
        <div className="pg-field">
          <label>Title</label>
          <input value={form.title} onChange={set("title")} placeholder="What are we developing?" />
        </div>
        <div className="pg-field">
          <label>Overarching goal</label>
          <textarea rows={3} value={form.goal} onChange={set("goal")} />
          <div className="pg-help">
            One or two sentences: what we're developing, for what application, and what decision this
            program leads to.
          </div>
        </div>
        <div className="pg-field">
          <label>Application / customer target</label>
          <input value={form.application} onChange={set("application")} />
        </div>
        <div className="pg-field">
          <label>Technical background</label>
          <textarea rows={4} value={form.technical_background} onChange={set("technical_background")} />
          <div className="pg-help">
            The "why" for reviewers outside the program: constraints, key levers, trade-offs. See
            AF-01 for a good example.
          </div>
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <div className="pg-field" style={{ flex: 1 }}>
            <label>Start date</label>
            <input type="date" value={form.start_date} onChange={set("start_date")} />
          </div>
          <div className="pg-field" style={{ width: 140 }}>
            <label>3-month phases</label>
            <select value={form.num_phases} onChange={set("num_phases")}>
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="pg-field">
          <label>Program owner (lead)</label>
          <select value={owner} onChange={(e) => setOwner(e.target.value)}>
            <option value="">— Unassigned —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.full_name || u.username}
                {u.role ? ` · ${u.role.replace(/_/g, " ")}` : ""}
              </option>
            ))}
          </select>
          <div className="pg-help">
            The lead (plus any Engineering Manager) can edit this program's
            criteria, risks, goals, gates, and sprints. You can change this later
            on the program page.
          </div>
        </div>
        <div className="pg-help" style={{ marginBottom: 10 }}>
          Phases, gates, and 2-week sprints are generated automatically. Completion criteria, gate
          criteria, and risks are filled in on the program page — undefined items stay flagged until
          they are.
        </div>
        {error && <div className="pg-error">{error}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="pg-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="pg-btn pg-btn-primary"
            disabled={busy || !form.code || !form.title || !form.goal}
            onClick={submit}
          >
            Create program
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProgramsTab() {
  const [programs, setPrograms] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState("");

  const load = () =>
    getPrograms()
      .then((data) =>
        setPrograms(
          Array.isArray(data) ? data : (data && Array.isArray(data.results) ? data.results : [])
        )
      )
      .catch((e) => setError(e.message));

  useEffect(() => {
    load();
  }, []);

  if (openId) {
    return (
      <ProgramDetail
        programId={openId}
        onBack={() => {
          setOpenId(null);
          load();
        }}
      />
    );
  }

  return (
    <div className="pg-wrap">
      <div className="pg-toolbar">
        <span className="pg-muted pg-small">
          {programs ? `${programs.length} program${programs.length === 1 ? "" : "s"}` : ""}
        </span>
        <button className="pg-btn pg-btn-primary" onClick={() => setShowCreate(true)}>
          + Add Program
        </button>
      </div>
      {error && <div className="pg-error">{error}</div>}
      {programs && programs.length === 0 && (
        <div className="pg-empty-slot" style={{ padding: 40 }}>
          No programs yet. Click "+ Add Program" to create one.
        </div>
      )}
      {(programs || []).map((p) => (
        <ProgramCard key={p.id} p={p} onOpen={setOpenId} />
      ))}
      {showCreate && (
        <CreateProgramModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            setShowCreate(false);
            setOpenId(id);
          }}
        />
      )}
    </div>
  );
}
